import { useConfig, useOidc } from '@revenuewell/front-end-bundle';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { w3cwebsocket } from 'websocket';
import { KazooUserApi, KazooUserRaw } from '../../services/api-client/kazoo-user-api';
import { KazooUser, KazooUserCredentials, VoiceApi } from '../../services/api-client/voice-api';
import { useLocations } from '../use-locations';
import { useProductsByLocation } from '../use-products-by-location';
import { UserInfoStatus } from './types';

export interface IKazooContext {
  userCredentials: KazooUserCredentials | null;
  userInfo: KazooUser | null;
  userInfoStatus: UserInfoStatus;
  kazooUsers: KazooUserRaw[];
  kazooClient: w3cwebsocket | null;

  kazooInit: () => Promise<void>;
  isStarted: () => boolean;
  connect: () => Promise<void>;
  deinitialize: () => Promise<void>;
  refreshKazooUserCredentials: () => Promise<void>;
  kazooFetchUsers: () => Promise<void>;
}

export const KazooContext = createContext<IKazooContext>(null!);

export function KazooProvider(props: React.PropsWithChildren<any>) {
  const { config } = useConfig();
  const { oidcService } = useOidc();
  const { selectedLocation } = useLocations();
  const { hasProduct, isLoading: isLoadingProducts } = useProductsByLocation(selectedLocation?.id);
  const [userCredentials, setUserCredentials] = useState<KazooUserCredentials | null>(null);
  const [userInfo, setUserInfo] = useState<KazooUser | null>(null);
  const [userInfoStatus, setUserInfoSatus] = useState<UserInfoStatus>('default');
  const [kazooUsers, setKazooUsers] = useState<KazooUserRaw[]>([]);
  const [kazooClient, setKazooClient] = useState<w3cwebsocket | null>(null);
  const voiceApi = new VoiceApi(config, oidcService);
  const kazooUserApi = new KazooUserApi();
  const [ready, setReady] = useState<boolean>(false);

  useEffect(() => {
    if (!selectedLocation || isLoadingProducts) return;

    (async () => {
      if (hasProduct('phone2')) {
        await kazooInit();
      }

      setReady(true);
    })();
  }, [selectedLocation, isLoadingProducts]);

  const kazooInit = async () => {
    try {
      setUserInfoSatus('pending');

      const [kazooUser, credentials] = await Promise.all([voiceApi.getUserInfo(), voiceApi.getKazooUserCredentials()]);

      setUserInfo(kazooUser);
      setUserCredentials(credentials);
      setUserInfoSatus('success');
    } catch (error) {
      setUserInfoSatus('error');
    }
  };

  const deinitialize = async () => {
    if (kazooClient) {
      try {
        const client = kazooClient;
        client.close();
        setKazooClient(null);
      } catch (ex) {
        console.error('ex : ', ex);
      }
    }
  };

  const refreshKazooUserCredentials = async () => {
    const credentials = await voiceApi.getKazooUserCredentials();
    setUserCredentials(credentials);
  };

  const kazooFetchUsers = async () => {
    const credentials = await voiceApi.getKazooUserCredentials();
    const users = await kazooUserApi.fetchUsers(
      credentials.kazooApiUrl,
      credentials.kazooAccountId,
      credentials.kazooUserToken
    );

    setKazooUsers(users);
  };

  const connect = async () => {
    const kazooUserCredentials = userCredentials;

    if (kazooUserCredentials) {
      const client = new w3cwebsocket(`${kazooUserCredentials.kazooApiWssUrl}/v2/websockets`);

      setKazooClient(client);

      const reconnect = async () => {
        if (kazooClient) {
          deinitialize();
          connect();
        }
      };

      client.onclose = (e: any) => {
        if (e.code === 1006) reconnect();
      };
    }
  };

  const contextValue = useMemo(
    () => ({
      userCredentials,
      userInfo,
      userInfoStatus,
      kazooUsers,
      kazooClient,

      kazooInit,
      isStarted: () => !!kazooClient,
      connect,
      deinitialize,
      refreshKazooUserCredentials,
      kazooFetchUsers
    }),
    [userCredentials, userInfo, userInfoStatus, kazooUsers, kazooClient]
  );

  return <KazooContext.Provider value={contextValue}>{ready && props.children}</KazooContext.Provider>;
}

export function useKazoo() {
  const context = useContext(KazooContext);

  if (!context) throw new Error('useKazoo must be used within the KazooProvider');

  return context;
}
